/**
 * Which GPT Live voice the ball opens the Line with, kept across reloads.
 * Bash is a he, so the server's default is masculine (LIVE_DEFAULT_VOICE);
 * this is the audition switch for picking among the rest: `?voice=<name>` on
 * any page stores the name (`?voice=default` forgets it) and every line after
 * opens with it. Only a name from LIVE_VOICES is ever stored or read back;
 * the door validates again. localStorage may be missing or throwing (private
 * mode, a blocked origin): reads answer null, writes are best effort.
 */
import { isLiveVoice, type LiveVoice } from "@bash/shared";

export const LIVE_VOICE_STORAGE_KEY = "bash.line.voice";
const QUERY_PARAM = "voice";
/** The one reserved word: `?voice=default` forgets the choice. */
const CLEAR_WORD = "default";

export function getLiveVoice(): LiveVoice | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(LIVE_VOICE_STORAGE_KEY);
    return isLiveVoice(v) ? v : null;
  } catch {
    return null;
  }
}

/** Stores a known voice, forgets on null; an unknown name changes nothing. */
export function setLiveVoice(v: string | null): void {
  if (typeof window === "undefined") return;
  if (v !== null && !isLiveVoice(v)) return;
  try {
    if (v === null) window.localStorage.removeItem(LIVE_VOICE_STORAGE_KEY);
    else window.localStorage.setItem(LIVE_VOICE_STORAGE_KEY, v);
  } catch {
    /* private mode or quota: the choice does not survive, the line still opens */
  }
}

/** Honour `?voice=<name>` (or `default`) on the page; the rest of the query is not ours. */
export function applyVoiceQueryParam(
  search: string = typeof window === "undefined" ? "" : window.location.search
): void {
  const v = new URLSearchParams(search).get(QUERY_PARAM);
  if (v !== null) setLiveVoice(v === CLEAR_WORD ? null : v);
}

/** Set once the stored choice has been copied to the server (Bash's phone, spec 2026-10-14 §6.1). */
export const LIVE_VOICE_MIGRATED_KEY = "bash.line.voice.migrated";

/**
 * The one-time copy: a voice chosen in this browser before the server kept one
 * goes to the server (only if the server has none), then the browser forgets
 * it, so the server's choice rules every call, the phone's included. While the
 * server cannot take it (the phone dark: a 404), the choice stays here and
 * keeps riding the open, as it always has. A later `?voice=` audition is
 * stored here again and still wins.
 */
export async function migrateLocalVoice(
  put: (voice: LiveVoice, onlyIfUnset: boolean) => Promise<void> = (v, o) =>
    import("@/lib/api/phone").then((m) => m.setVoice(v, o))
): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(LIVE_VOICE_MIGRATED_KEY) === "1") return;
    const v = getLiveVoice();
    if (v) await put(v, true);
    // A `?voice=` stored while the copy was in flight is an audition: it stays.
    if (getLiveVoice() === v) {
      window.localStorage.removeItem(LIVE_VOICE_STORAGE_KEY);
    }
    window.localStorage.setItem(LIVE_VOICE_MIGRATED_KEY, "1");
  } catch {
    /* the phone is dark or the call failed: keep the choice, try again next load */
  }
}
